import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  TablePagination,
} from "@mui/material";
// components
import { sendDataWithGet } from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";

import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import MoreMenu from "../../components/MPI/MoreMenu";
import CreateDialog from "../../components/MPI/Dialogs/CreateDialog";
import EditDialog from "../../components/MPI/Dialogs/EditDialog";
import DeleteDialog from "../../components/MPI/Dialogs/DeleteDialog";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";

// ----------------------------------------------------------------------

const defaultValue = {};

const TABLE_HEAD = [
  { id: "id", label: "ID" },
  { id: "acquirer_bin", label: "Acquirer BIN" },
  { id: "acquirer_merchant_id", label: "Acquirer Merchant ID" },
  { id: "action" },
];

export default function MPITable({ userRole }) {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({ results: [], count: 0 });
  const [filter, setFilter] = useState({});
  const [openEditDetailDialog, setOpenEditDetailDialog] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  // ------------------ filterStuffies --------------------//

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryList = (filter) => {
    loaded && setLoaded(false);
    queryPage(filter);
  };

  const queryPage = (filterParam, pageParams = pagination) => {
    const params = { ...filterParam };
    
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const hasFilters = Object.keys(params).length > 0;

    if (pageParams.page) params.page = pageParams.page;
    if (pageParams.page_size) params.page_size = pageParams.page_size;
    const queryString = new URLSearchParams(params).toString();

    const url = `/mpi_details/${hasFilters ? 'filter/' : 'list/'}?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithGet(url, setTableData, setLoaded);
  };

  const handleChangePage = (event, newPage) => {
    const newPagination = { ...pagination, page: newPage + 1 };
    setPagination(newPagination);
    queryPage(filter, newPagination);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPagination = { page: 1, page_size: parseInt(event.target.value, 10) };
    setPagination(newPagination);
    queryPage(filter, newPagination);
  };

  const onFilterSubmit = (fParams = filter) => {
    const params = { ...fParams };
    if (params.acquirer_merchant_id) {
      params.acquirer_merchant_id = params.acquirer_merchant_id.trim();
    }
    setFilter(params);
    setPagination({ ...pagination, page: 1 });
    queryPage(params, { ...pagination, page: 1 });
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = (tableData?.results || []).length === 0;

  const filteredList = applySortFilter(tableData?.results || [], order, orderBy);

  const schema = [
    {
      type: "text",
      label: "Acquirer Merchant ID",
      param: "acquirer_merchant_id",
    },
  ];

  return (
    <Box>
      {userRole?.includes("Add") && (
        <CreateDialog cb={onFilterSubmit} />
      )}

      <FormSearch
        schema={schema}
        loading={true}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const { id, acquirer_bin, acquirer_merchant_id } = row;

                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={id} />
                        <CustomTableCell field={acquirer_bin} />
                        <CustomTableCell field={acquirer_merchant_id} />
                        <TableCell align="right">
                          <MoreMenu
                            userRole={userRole}
                            openEditDialog={() => {
                              setGlobalLoading(true);
                              const url = `/mpi_details/${id}/detail/`;
                              sendDataWithGet(
                                url,
                                setOpenEditDetailDialog,
                                () => setGlobalLoading(false)
                              );
                            }}
                            openDeleteDialog={() => setOpenDeleteDialog(id)}
                          />
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={tableData?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      ) : (
        <CircularLoader />
      )}

      {openEditDetailDialog && (
        <EditDialog
          cb={onFilterSubmit}
          data={openEditDetailDialog}
          onClose={() => setOpenEditDetailDialog(null)}
        />
      )}

      {openDeleteDialog && (
        <DeleteDialog
          cb={onFilterSubmit}
          id={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(null)}
        />
      )}
    </Box>
  );
}
